// Copyright ©️ 2026 eVolve MEP, LLC

import type { FocusEvent } from 'react';

import type { BendInputKey, BendTypeInput } from '@evolvemep/api-ts';
import { Text, useMantineTheme } from '@mantine/core';

import { AngleInput } from 'components/Mantine/AngleInput';
import { StepperInput } from 'components/Mantine/StepperInput';
import { toHighlightableObjectName } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { useBendPreviewContext } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { getCenterBendKeys } from 'modules/Shop/EvolveFabrication/BendCreator/State/centerBend';

/** Standard angles offered as quick-fill buttons, in degrees. */
const QUICK_FILL_ANGLES = [10, 22.5, 30, 45, 60] as const;

/** Heights step by a quarter inch; every other length steps by an inch. In decimal feet, the API's unit. */
const QUARTER_INCH_STEP_KEYS: readonly BendInputKey[] = ['offsetHeight', 'kickHeight', 'saddleHeight'];
const QUARTER_INCH_IN_FEET = 0.25 / 12;
const ONE_INCH_IN_FEET = 1 / 12;

type Props = {
  bendTypeInput: BendTypeInput;
  isStockLengthField?: boolean;
};

export const BendDimensionInput = ({
  bendTypeInput: { name, keyName, isRequired, isAngle },
  isStockLengthField,
}: Props) => {
  const {
    values: { maximizeLegLength: isUsingStockLength, centerBend, bendType },
  } = useBendCreatorFormContext();
  const { highlightedObject, setHighlightedObject } = useBendPreviewContext();
  const labelObjectName = toHighlightableObjectName(name);
  const theme = useMantineTheme();

  const { wrappedGetInputProps } = useBendCreatorValidationContext();

  const labelColor = highlightedObject === labelObjectName ? theme.primaryColor : undefined;
  const {
    onFocus: formOnFocus,
    onBlur: formOnBlur,
    ...formInputProps
  } = wrappedGetInputProps(keyName, {
    validateOnChange: true,
  });
  // Hovering or focusing a field highlights its dimension on the model; focus is what a touch screen has.
  const fieldProps = {
    ...formInputProps,
    onMouseEnter: () => setHighlightedObject(labelObjectName),
    onMouseLeave: () => setHighlightedObject(null),
    onFocus: (event: FocusEvent<HTMLInputElement>) => {
      // oxlint-disable-next-line typescript/no-unsafe-call
      formOnFocus?.(event);
      setHighlightedObject(labelObjectName);
    },
    onBlur: (event: FocusEvent<HTMLInputElement>) => {
      // oxlint-disable-next-line typescript/no-unsafe-call
      formOnBlur?.(event);
      setHighlightedObject(null);
    },
  };

  return isAngle ? (
    <AngleInput
      label={
        <Text span inherit c={labelColor}>
          {name}
        </Text>
      }
      required={isRequired}
      quickFillAngles={QUICK_FILL_ANGLES}
      min={0.01}
      max={90}
      placeholder="Enter degrees"
      {...fieldProps}
    />
  ) : (
    <StepperInput
      mode="inches"
      valueInFeet
      label={
        <Text span inherit c={labelColor}>
          {name}
        </Text>
      }
      required={isRequired}
      allowBlank={!isRequired}
      min={0}
      step={QUARTER_INCH_STEP_KEYS.includes(keyName) ? QUARTER_INCH_IN_FEET : ONE_INCH_IN_FEET}
      placeholder="Inches"
      {...fieldProps}
      {...(isStockLengthField && isUsingStockLength
        ? {
            disabled: true,
            readOnly: true,
            description: 'This field will use the remaining stock length.',
          }
        : {})}
      {...(centerBend && getCenterBendKeys(bendType).includes(keyName)
        ? { readOnly: true, description: 'Set by Center Bend' }
        : {})}
    />
  );
};
